import type Guid from "@/app/types/common/guid";
import { GuidMap } from "@/app/types/common/guidcollections";
import { distinctGuids, isNotNullOrUndefined, toGuidMap } from "@/app/apiutils";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { Maybe } from "@/components/utils/typeHelpers";

/**
 * Cache Utility For Loading items by ids, but only as required.
 */
export class PiecemealByGuidsCache<T> {
  private _map = makeRef(new GuidMap<T>());
  private _extractId: (item: T) => Guid;
  private _loadItems: (ids: readonly Guid[]) => Promise<readonly T[]>;
  private _inProgressLoad?: Promise<readonly T[]>;

  public constructor(
    extractId: (item: T) => Guid,
    loadItems: (ids: readonly Guid[]) => Promise<readonly T[]>
  ) {
    this._extractId = extractId;
    this._loadItems = loadItems;
  }

  async ensureLoaded(ids: readonly Guid[]) {
    // To avoid sending parallel load requests for the same ids
    // we wait for any in progress load to complete
    // before evaluating the ids.
    // If multiple are queued then when one completes only one more will become inProgress
    // the others will wait in this while loop.
    while (this._inProgressLoad) {
      await this._inProgressLoad;
    }
    const toLoad = distinctGuids(ids).filter((id) => !this._map.value.has(id));
    if (toLoad.length) {
      const promise = this._loadItems(toLoad).then((results) => {
        this._inProgressLoad = undefined;
        return results;
      });
      this._inProgressLoad = promise;
      const toAdd = await promise;
      this._map.value = toGuidMap(
        this._map.value.values().concat(toAdd),
        this._extractId
      );
    }
  }

  get(id: Maybe<Guid>) {
    return this._map.value.get(id);
  }

  getByIds(ids: readonly Maybe<Guid>[]) {
    return ids
      .map((id) => this._map.value.get(id))
      .filter(isNotNullOrUndefined);
  }

  values() {
    return this._map.value.values();
  }
}
